import React from 'react';
import {
  ErrorMessage,
  Modal,
  PrimaryButton,
  SecondaryButton,
} from '../../common';
import { useTranslationsController } from '../../../utils/globalTranslationsController';
import { onClickOpenUrl } from '../../../utils/link.utils';
import { useNavigation } from '../Navigation/useNavigation';

interface SelfBillTermsProps {
  isOpen: boolean;
  acceptTerms: () => void;
  isLoading: boolean;
  error?: string;
}

interface AcceptTermsProps
  extends Omit<SelfBillTermsProps, 'isOpen' | 'error'> {
  text: string;
}

const translations = {
  selfbillterms: {
    'SELFBILL.MODAL.TITLE': 'Opt-in to Self-Billing',
    'SELFBILL.MODAL.TEXT':
      'To simplify your payments, we are introducing self-billing. By opting in, you authorise us to generate invoices on your behalf. This ensures faster payments and compliance with tax regulations.',
    'SELFBILL.MODAL.TERMS_BUTTON': 'Read Full Terms',
    'SELFBILL.MODAL.ACCEPT_BUTTON': 'I agree to the Terms',
  },
};

const ReadTerms: React.FC<{ text: string }> = ({ text }) => {
  const { language } = useNavigation();
  const link = `/about/terms${language ? '.' + language : ''}.html#selfbilling`;
  return (
    <SecondaryButton
      onClick={onClickOpenUrl(link)}
      data-testid={'t_read_terms'}
    >
      {text}
    </SecondaryButton>
  );
};
const AcceptTerms: React.FC<AcceptTermsProps> = ({
  text,
  acceptTerms,
  isLoading,
}) => {
  return (
    <PrimaryButton
      onClick={acceptTerms}
      disabled={isLoading}
      aria-disabled={isLoading}
      data-testid={'t_accept_terms'}
    >
      {text}
    </PrimaryButton>
  );
};

export const SelfBillTerms: React.FC<SelfBillTermsProps> = ({
  isOpen,
  error,
  ...rest
}) => {
  const { selfbillterms: t } = useTranslationsController(translations);
  return (
    <Modal
      isOpen={isOpen}
      fullScreen={false}
      titleText={t['SELFBILL.MODAL.TITLE']}
      actions={
        <>
          <ReadTerms text={t['SELFBILL.MODAL.TERMS_BUTTON']} />
          <AcceptTerms text={t['SELFBILL.MODAL.ACCEPT_BUTTON']} {...rest} />
        </>
      }
    >
      <>{t['SELFBILL.MODAL.TEXT']}</>
      {error && <ErrorMessage message={error} />}
    </Modal>
  );
};
